import { Link } from 'react-router-dom'
import { Alert, Card, CardBody } from '@/components/ui/primitives'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useParishSettings } from '@/layouts/PublicLayout'

/*
 * Paginas institucionais do portal.
 *
 * Inicio, noticias, horarios e eventos ficam em arquivos proprios; aqui estao
 * contato, privacidade e o 404.
 */

export function ContatoPage() {
  useDocumentTitle('Contato')
  const { data: parish } = useParishSettings()

  const redes = [
    { url: parish?.instagramUrl, nome: 'Instagram' },
    { url: parish?.facebookUrl, nome: 'Facebook' },
    { url: parish?.youtubeUrl, nome: 'YouTube' },
  ].filter((rede): rede is { url: string; nome: string } => !!rede.url)

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="font-serif text-3xl font-semibold">Contato</h1>

      <Card className="mt-6">
        <CardBody className="space-y-3">
          {parish?.address && (
            <p>
              <strong>Endereco:</strong> {parish.address}
              {parish.city ? `, ${parish.city} — ${parish.state}` : ''}
            </p>
          )}
          {parish?.phone && (
            <p>
              <strong>Telefone:</strong>{' '}
              <a href={`tel:${parish.phone.replace(/\D/g, '')}`} className="underline">
                {parish.phone}
              </a>
            </p>
          )}
          {parish?.whatsapp && (
            <p>
              <strong>WhatsApp:</strong>{' '}
              <a
                href={`https://wa.me/55${parish.whatsapp.replace(/\D/g, '')}`}
                className="underline"
                rel="noopener noreferrer"
                target="_blank"
              >
                {parish.whatsapp}
              </a>
            </p>
          )}
          {parish?.email && (
            <p>
              <strong>E-mail:</strong>{' '}
              <a href={`mailto:${parish.email}`} className="underline">
                {parish.email}
              </a>
            </p>
          )}
          {parish?.officeHours && (
            <p>
              <strong>Atendimento da secretaria:</strong> {parish.officeHours}
            </p>
          )}

          {redes.length > 0 && (
            <p className="flex flex-wrap gap-4 pt-2">
              {redes.map((rede) => (
                <a
                  key={rede.nome}
                  href={rede.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline"
                >
                  {rede.nome}
                </a>
              ))}
            </p>
          )}

          {!parish?.address && !parish?.phone && !parish?.email && (
            <Alert tone="info">
              Os dados de contato ainda nao foram preenchidos pela secretaria.
            </Alert>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

/** LGPD: pagina exigida pelo consentimento coletado nas inscricoes. */
export function PrivacidadePage() {
  useDocumentTitle('Politica de privacidade')

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="font-serif text-3xl font-semibold">Politica de privacidade</h1>

      <div className="conteudo-rico mt-6">
        <h2>Quais dados coletamos</h2>
        <p>
          A Paroquia de Pacaembu coleta apenas os dados necessarios para organizar suas
          atividades: nome, telefone e, quando informado, e-mail e data de nascimento de quem se
          inscreve em eventos. <strong>Nenhum dado de pagamento e coletado neste site</strong> — o
          pagamento de eventos e sempre presencial.
        </p>

        <h2>Para que usamos</h2>
        <p>
          Os dados sao usados exclusivamente para a organizacao do evento em que a pessoa se
          inscreveu: confirmar a inscricao, organizar listas de presenca e entrar em contato em
          caso de alteracao. Nao vendemos, nao cedemos e nao compartilhamos esses dados com
          terceiros.
        </p>

        <h2>Inscritos menores de idade</h2>
        <p>
          Inscricoes de menores de 18 anos exigem o nome e o telefone de um responsavel legal,
          registrados junto com a inscricao.
        </p>

        <h2>Consentimento</h2>
        <p>
          No momento da inscricao, registramos a data, o horario e o texto exato do consentimento
          que foi aceito. Assim fica sempre possivel saber a que cada pessoa consentiu, mesmo que
          o texto do formulario mude depois.
        </p>

        <h2>Seus direitos</h2>
        <p>
          Conforme a Lei Geral de Protecao de Dados (Lei 13.709/2018), voce pode solicitar a
          qualquer momento o acesso, a correcao ou a exclusao dos seus dados. Basta procurar a
          secretaria paroquial pelos contatos da <Link to="/contato">pagina de contato</Link>.
        </p>

        <h2>Registro de acessos</h2>
        <p>
          Por seguranca, o sistema registra data, hora e endereco de rede das operacoes feitas por
          membros da equipe no painel administrativo, incluindo qualquer exportacao de lista de
          inscritos. Esses registros sao internos e servem apenas para auditoria.
        </p>
      </div>
    </div>
  )
}

export function NaoEncontradaPage() {
  useDocumentTitle('Pagina nao encontrada')

  return (
    <div className="mx-auto max-w-3xl px-4 py-16 text-center">
      <p className="font-serif text-5xl font-semibold text-primario">404</p>
      <h1 className="mt-4 font-serif text-2xl font-semibold">Pagina nao encontrada</h1>
      <p className="mt-2 text-suave">
        O endereco pode ter mudado ou o conteudo pode ter sido removido.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-4">
        <Link to="/" className="font-medium underline">
          Ir para o inicio
        </Link>
        <Link to="/horarios" className="font-medium underline">
          Ver horarios de missa
        </Link>
        <Link to="/noticias" className="font-medium underline">
          Ver noticias
        </Link>
      </div>
    </div>
  )
}
